import { type Query, useQueries, useQueryClient } from '@tanstack/react-query';
import { COLLECTION_SCORING_WINDOW_MS, type CollectionImagesPairParams, pollWhilePending } from '@vizit/api';
import { collectionQueryPredicate, useGetCollectionScoringQuery } from '@vizit/api';
import { type SearchCollectionImagesQuery, useSearchCollectionImagesQuery } from '@vizit/api';
import { useToast } from '@vizit/atoms';
import * as React from 'react';
import { scoringReadyToast, scoringStartedToast } from './collection-scoring-copy';
import { uploadScoredToast, uploadScoringToast } from './collection-scoring-copy';
import { INITIAL_COLLECTION_SCORING_NOTICES, collectionScoringNoticesReducer } from './collection-scoring-notices';
import type { ObservedScoring } from './collection-scoring-notices';
import { collectionScoringStatusRefetchInterval } from './use-collection-scorings.polling';

const EMPTY_IMAGE_IDS: string[] = [];

/** The listing scores at most this many images against a pair per read. */
const PAIR_PAGE_LIMIT = 100; // score-threshold-allow: a page size (the API's pair-score page cap), not a score

interface UseCollectionScoringNoticesProps {
  workspaceId?: string;
  collectionId: string;
  /** The benchmark on screen; `null` while there is none */
  benchmarkKey: string | null;
  /** Its listed scorings, as the page names them */
  scorings: ObservedScoring[];
  /** Whether that list has been read for this benchmark */
  isResolved: boolean;
  /** The pair on screen; an upload is scored once its images carry a score under it */
  pair: CollectionImagesPairParams | null;
  /** "View scores" on a landed category: shows it, switching back to its benchmark when needed */
  onViewScores: (benchmarkKey: string, categoryId: string) => void;
}

/**
 * The collection page's scoring toasts, driven by `collectionScoringNoticesReducer`: "Scoring against…" when the user
 * adds categories, "{category} is ready" when a run they saw lands (under any benchmark: each tracked run's status
 * route is polled, sharing the cache of the scorings hook's own polls), and one "Scoring N new assets" / "N new
 * assets scored" pair per upload batch. Returns the actions the page calls and the upload the scorings list waits
 * on for the benchmark on screen.
 */
function useCollectionScoringNotices({
  workspaceId,
  collectionId,
  benchmarkKey,
  scorings,
  isResolved,
  pair,
  onViewScores,
}: UseCollectionScoringNoticesProps) {
  const [state, dispatch] = React.useReducer(collectionScoringNoticesReducer, INITIAL_COLLECTION_SCORING_NOTICES);
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (benchmarkKey && isResolved) {
      dispatch({ type: 'observed', benchmarkKey, scorings });
    }
  }, [benchmarkKey, isResolved, scorings]);

  // One status poll per tracked run, whichever benchmark it belongs to; each stops once its run lands.
  const tracked = Object.values(state.tracked);
  const landed = useQueries({
    queries: tracked.map((scoring) => {
      const variables = { workspace_id: workspaceId ?? '', collection_id: collectionId, scoring_id: scoring.id };
      return {
        queryKey: [...useGetCollectionScoringQuery.getKey(variables), scoring.requestedAt],
        queryFn: useGetCollectionScoringQuery.fetcher(variables),
        enabled: !!workspaceId,
        refetchInterval: collectionScoringStatusRefetchInterval(variables),
      };
    }),
    combine: (results) =>
      results.flatMap((result, index) => {
        const status = result.data?.status;
        const run = tracked[index];
        return run &&
          status &&
          status !== 'pending' &&
          Date.parse(result.data?.requestedAt ?? '') === Date.parse(run.requestedAt)
          ? [{ id: run.id, status }]
          : [];
      }),
  });
  // Keyed on what landed, not on the array, which is rebuilt every render. Scoring ids are UUIDs, so the separators
  // cannot occur in them.
  const landedKey = landed.map(({ id, status }) => `${id}:${status}`).join(',');
  React.useEffect(() => {
    if (!landedKey) {
      return;
    }
    for (const entry of landedKey.split(',')) {
      const [id = '', status] = entry.split(':');
      dispatch({ type: 'settled', id, status: status === 'failed' ? 'failed' : 'ready' });
    }
  }, [landedKey]);

  // The images of the batch on screen still without a score under the pair; read together, at most a page of them.
  const batch = benchmarkKey ? state.uploads[benchmarkKey] : undefined;
  const unscoredImageIds = React.useMemo(
    () =>
      batch && !batch.isAnnounced
        ? batch.imageIds.filter((id) => !batch.scoredImageIds.includes(id)).slice(0, PAIR_PAGE_LIMIT)
        : [],
    [batch],
  );
  const submittedAt = batch?.submittedAt ?? null;
  const { data: uploadedImages } = useSearchCollectionImagesQuery(
    {
      workspace_id: workspaceId ?? '',
      collection_id: collectionId,
      image_ids: unscoredImageIds,
      limit: Math.max(unscoredImageIds.length, 1),
      ...pair,
    },
    {
      enabled: !!workspaceId && !!pair && unscoredImageIds.length > 0,
      refetchInterval: pollWhilePending<Query<SearchCollectionImagesQuery>>(
        (query) =>
          submittedAt && (query.state.data?.data ?? []).some((image) => !image.pair_score?.scored) ? [submittedAt] : [],
        { staleTimeoutMs: COLLECTION_SCORING_WINDOW_MS },
      ),
    },
  );
  const scoredUploadKey = (uploadedImages?.data ?? [])
    .filter(
      (image) => image.pair_score?.scored && pair && image.pair_score.product_category_id === pair.product_category_id,
    )
    .map((image) => image.id)
    .join(',');
  React.useEffect(() => {
    if (benchmarkKey && scoredUploadKey) {
      dispatch({ type: 'imagesScored', benchmarkKey, imageIds: scoredUploadKey.split(',') });
    }
  }, [benchmarkKey, scoredUploadKey]);

  // The toast outlives this render; its action calls whatever handler is current when pressed.
  const onViewScoresRef = React.useRef(onViewScores);
  React.useLayoutEffect(() => {
    onViewScoresRef.current = onViewScores;
  }, [onViewScores]);

  // Each notice once, even if this effect runs again before the outbox is emptied.
  const shownRef = React.useRef(0);
  React.useEffect(() => {
    const fresh = state.outbox.filter((notice) => notice.id > shownRef.current);
    if (fresh.length === 0) {
      return;
    }
    for (const notice of fresh) {
      if (notice.kind === 'ready') {
        const { benchmarkKey: landedKey, categoryId } = notice;
        addToast(scoringReadyToast(notice.name, () => onViewScoresRef.current(landedKey, categoryId)));
      } else if (notice.kind === 'uploadStarted') {
        addToast(uploadScoringToast(notice.assetCount));
      } else {
        addToast(uploadScoredToast(notice.assetCount));
        // The tiles of the new images show their scores, and stop being dimmed.
        void queryClient.invalidateQueries({
          predicate: collectionQueryPredicate(['SearchCollectionImagesInfinite'], collectionId),
        });
      }
    }
    const throughId = fresh[fresh.length - 1]?.id ?? shownRef.current;
    shownRef.current = throughId;
    dispatch({ type: 'delivered', throughId });
  }, [state.outbox, addToast, queryClient, collectionId]);

  // An upload the API never answers (nothing to score again, or a run that never lands) stops waiting after the
  // scoring window, silently.
  React.useEffect(() => {
    const timers = Object.entries(state.uploads).map(([key, batch]) =>
      setTimeout(
        () => dispatch({ type: 'expired', benchmarkKey: key, submittedAt: batch.submittedAt }),
        Math.max(Date.parse(batch.submittedAt) + COLLECTION_SCORING_WINDOW_MS - Date.now(), 0),
      ),
    );
    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }
    };
  }, [state.uploads]);

  /** Announces categories the user just added that are now scoring, and tracks them. */
  const trackRequested = React.useCallback(
    (requested: ObservedScoring[]) => {
      if (!benchmarkKey) {
        return;
      }
      dispatch({ type: 'requested', benchmarkKey, scorings: requested });
      const started = requested.filter((scoring) => scoring.status === 'pending');
      if (started.length > 0) {
        addToast(scoringStartedToast(started.map((scoring) => scoring.name)));
      }
    },
    [benchmarkKey, addToast],
  );

  /**
   * Announces assets uploaded into a collection scored under categories and waits for the API to score them.
   * Without categories nothing is scored, so nothing is said.
   */
  const trackUpload = React.useCallback(
    (assetCount: number) => {
      if (!benchmarkKey || assetCount === 0 || scorings.length === 0) {
        return;
      }
      dispatch({ type: 'uploaded', benchmarkKey, assetCount, submittedAt: new Date().toISOString(), scorings });
    },
    [benchmarkKey, scorings],
  );

  const upload = React.useMemo(
    () =>
      batch && !batch.isAnnounced
        ? { submittedAt: batch.submittedAt, baselineRequestedAt: batch.baselineRequestedAt }
        : null,
    [batch],
  );

  /** Takes a failed upload out of the batch on screen: its image never gets a score. */
  const trackFailedImage = React.useCallback(
    (imageId?: string) => {
      if (benchmarkKey) {
        dispatch({ type: 'imageFailed', benchmarkKey, imageId });
      }
    },
    [benchmarkKey],
  );

  /** The images of the open batch on screen still waiting for a score under the pair; their tiles stay dimmed. */
  const waitingImageIds = React.useMemo(
    () =>
      batch && !batch.isAnnounced ? batch.imageIds.filter((id) => !batch.scoredImageIds.includes(id)) : EMPTY_IMAGE_IDS,
    [batch],
  );

  /** Adds an uploaded image to the batch on screen, once its record is created. */
  const trackUploadedImage = React.useCallback(
    (imageId: string) => {
      if (benchmarkKey) {
        dispatch({ type: 'imageUploaded', benchmarkKey, imageId });
      }
    },
    [benchmarkKey],
  );

  return {
    trackRequested,
    trackUpload,
    trackUploadedImage,
    trackFailedImage,
    waitingImageIds,
    /** The upload the scorings list on screen waits on, or `null` */
    upload,
  };
}

export { useCollectionScoringNotices };
export type { UseCollectionScoringNoticesProps };
